import { createSelector } from 'reselect';

import {
	CLEAR_ALL,
	CLEAR_RESULT,
	EXECUTE_DEPOSIT,
	EXECUTE_DEPOSIT_PRECHECK,
	GENERAL_ERROR,
	GET_PAYMENTS_TAGS,
	HYDRATED_DETAILS,
	INIT_SPEC,
	LOGOUT,
	PROMPT_KYC,
	PROMPT_SECUREID,
	SELECT_DEFAULT_AMOUNT,
	SELECT_PAYMENT_METHOD,
	SHOP_ITEM_FOR_DEPOSIT,
	SHOW_DEPOSIT_LIMIT_OPTION,
	UPDATE_AMOUNT,
	UPDATE_AUTOSKIP,
	UPDATE_FEES_AND_LIMITS,
	UPDATE_PAYMENT_METHODS,
	UPDATE_SPEC,
	VALIDATE_SPEC
} from '@comeon/actions-public';
import reducerRegistry from '@comeon/reducer-registry';

import { getDepositOffers, isDepositBonusOk } from '~/components/bonus/ducks/index';
import { CLEAR_BONUS, SELECT_BONUS, SELECT_BONUS_PACKAGE } from '~/components/bonus/ducks/constants';
import { selectPaymentMethod, updateDetails } from '~/components/payment/deposit-beta/pages/deposit-method/ducks';
import {
	createBonusParams,
	generalError,
	getPAGdata,
	validateSpec,
	wrappedRequestApi
} from '~/components/payment/ducks';
import { applePaySupportedMethodCodes, isApplePaySupported } from '~/components/payment/util/apple-pay';

import { hydratePendingWithdrawals } from './pendingwithdrawals';

export const REDUCER_NAME = 'payment';

const AGREEMENT_CHECK = '@komigen/mobile-frontend/payment/AGREEMENT_CHECK';
const HYDRATED_PAYMENT_METHODS = '@komigen/mobile-frontend/payment/HYDRATED_PAYMENT_METHODS';
const UPDATE_PAYMENT_DATA = '@komigen/mobile-frontend/payment/UPDATE_PAYMENT_DATA';
const APPLEPAY_PROMPT_SECUREID = '@komigen/mobile-frontend/payment/APPLEPAY_PROMPT_SECUREID';
const INIT_URL_FEES_AND_LIMITS = '@komigen/mobile-frontend/payment/INIT_URL_FEES_AND_LIMITS';
const SELECT_ACCOUNT = '@komigen/mobile-frontend/payment/SELECT_ACCOUNT';
const UPDATE_STATUS = '@komigen/mobile-frontend/payment/UPDATE_STATUS';
const USING_HYDRATED_PAYMENT_METHODS = '@komigen/mobile-frontend/payment/USING_HYDRATED_PAYMENT_METHODS';
const VERIFY_AGE = '@komigen/mobile-frontend/payment/VERIFY_AGE';
const UPDATE_SUBMIT_BUTTON_VALIDATION = '@komigen/mobile-frontend/payment/UPDATE_SUBMIT_BUTTON_VALIDATION';
const CLEAR_BONUS_SPEC = '@komigen/mobile-frontend/payment/CLEAR_BONUS_SPEC';
const CHANGE_LAST_USED_ACCOUNT = '@komigen/mobile-frontend/payment/CHANGE_LAST_USED_ACCOUNT';

/**
 * [ShowDepositLimitOption MGA Players sometimes require a link to set daily deposit limit. This
 * request decides if so is the case.]
 * @return {[type]} [description]
 */
async function getShowDepositLimitOption(dispatch) {
	const url = '/PaymentFormDataService/showDepositLimitOption';
	return wrappedRequestApi({ endpoint: url }, dispatch);
}

function getPaymentMethodTags() {
	return async dispatch => {
		const url = '/PaymentFormDataService/getPaymentMethodTags';
		const response = await wrappedRequestApi({ endpoint: url }, dispatch);
		dispatch({
			type: GET_PAYMENTS_TAGS,
			payload: response
		});
	};
}
async function getCVVBypass(dispatch, playerCardId, amount, providerId, paymentMethodId) {
	const url = `/CreditCardIntegration/client/rest/bypass/cvv/isBypass`;
	const params = {
		playerCardId,
		amount,
		providerId,
		paymentMethodId
	};
	const result = await wrappedRequestApi(
		{
			endpoint: url,
			payload: params,
			method: 'POST'
		},
		dispatch
	);

	return result;
}

function updatePaymentStatusSpec(payload) {
	return dispatch => {
		dispatch({
			type: UPDATE_SPEC,
			payload
		});
		return dispatch(validateSpec());
	};
}

function changeLastUsedAccountIdx(payload) {
	return dispatch => {
		dispatch({ type: CHANGE_LAST_USED_ACCOUNT });
		return dispatch(updateDetails(payload));
	};
}

function confirmGeneralError(payload) {
	return dispatch => {
		dispatch({
			type: GENERAL_ERROR,
			payload: { confirmed: true, payload }
		});
	};
}

function sortById(arr) {
	let obj = null;
	let key = null;
	if (arr && arr.length > 0) {
		obj = {};
		arr.forEach(elm => {
			key = elm.methodId;
			obj[key] = elm;
		});
	}
	return obj;
}

function filterMethodsForABtest(arr) {
	try {
		const disabledMethods = JSON.parse(sessionStorage.getItem('disabledDepositMethods'));
		return disabledMethods ? arr.filter(method => !disabledMethods.includes(method.methodId)) : arr;
	} catch (e) {
		console.log(e); // eslint-disable-line no-console
		return arr;
	}
}

async function transformMethodsResponse({ paymentMethods, playerCurrency }) {
	const filteredMethods = filterMethodsForABtest(paymentMethods);
	const hasApplePaySupport = await isApplePaySupported(playerCurrency);
	const tranformedMethods = [];

	filteredMethods.forEach(method => {
		const isApplePay = applePaySupportedMethodCodes.includes(method.methodCode);

		if (isApplePay && !hasApplePaySupport) {
			// eslint-disable-next-line no-console
			console.warn('payment:hydratePaymentMethods: applePay is not supported and was filtered');
			return;
		}

		// For now it is hardcoded
		const isAmountRestricted = ['SASONO'].includes(method.methodCode);
		tranformedMethods.push({ ...method, isApplePay, isAmountRestricted });
	});

	const objSortedOnMethodId = sortById(tranformedMethods);
	objSortedOnMethodId.order = tranformedMethods.map(paymentMethod => paymentMethod.methodId);

	return objSortedOnMethodId;
}

function sortByFormTemplateCode(arr) {
	/*
	Sort on method-template-type.
	templatecode is the type of the method, for example
	CREDIT-CARD, EMAIL-CURRENCIES, CLICK-TO-PAY, TWO-FIELDS, NETELLER, BANKWIRE
	A selected method has one of these types.
	*/
	const obj = {};
	if (arr && arr.length > 0) {
		arr.forEach(elm => {
			const key = elm.formTemplateCode;
			obj[key] = elm.formTemplateMobileUrl;
		});
	}
	return obj;
}

async function handleBlikAlias(dispatch) {
	const endpoint = '/DotpayIntegration/blik/alias/status';
	return wrappedRequestApi({ endpoint }, dispatch);
}

async function handleTpayBlikAlias(dispatch) {
	const endpoint = '/TpayIntegration/client/blik/alias/status';
	return wrappedRequestApi({ endpoint }, dispatch);
}

// fix me
function hydratePaymentMethods({ oneButtonFlow = false } = {}) {
	return async (dispatch, getState) => {
		const {
			payment: { hydrated = false }
		} = getState();
		const { isDesktop } = getState().brand;
		const { isRegistration } = getState().account;
		const bonusState = getState().bonuses;
		const bonusParams = createBonusParams(bonusState);
		if (hydrated) {
			return dispatch({ type: USING_HYDRATED_PAYMENT_METHODS });
		}
		dispatch({
			type: HYDRATED_PAYMENT_METHODS,
			payload: {
				pending: true,
				hydrated: false,
				paymentMethods: null,
				formTemplateTypes: null,
				lastUsedMethodDetails: null,
				playerCurrency: null,
				spec: null,
				status: null,
				methodDetails: null,
				feesAndLimits: null,
				fallbackPaymentMethodIds: null
			}
		});
		const response = await wrappedRequestApi(
			{
				endpoint: `/PaymentFormDataService/getDepositPaymentMethods?isMobileDevice=${!isDesktop}`
			},
			dispatch
		);
		/* validate response */
		const valid =
			response && response.paymentMethods && response.paymentMethods.length && response.paymentMethods.length > 0;
		if (!valid) {
			return dispatch(generalError('methods'));
		}
		const { playerCurrency } = response;
		const methodsObject = await transformMethodsResponse(response);

		const normalizedPaymentMethod = {
			entities: { paymentMethods: methodsObject }
		};
		const normalizedFormTemplates = {
			entities: {
				formTemplateTypes: sortByFormTemplateCode(response.formTemplateTypes)
			}
		};
		const { lastPaymentMethodId } = response.lastUsedMethodDetails;

		const PAGdata = getPAGdata(window.location.search);
		const autoSelectMethodId = PAGdata && JSON.parse(PAGdata).methodId;
		const isBankIdDeposit = PAGdata && JSON.parse(PAGdata).oneButtonFlow;
		const selectedMethodID = autoSelectMethodId || lastPaymentMethodId;
		/* if applepay was the last method and this device do not support it,
		 it is replaced */
		const lastMethodIsInList = normalizedPaymentMethod.entities.paymentMethods[selectedMethodID];
		const hasCreditCard = normalizedPaymentMethod.entities.paymentMethods[1];
		const fallbackMethodId = hasCreditCard ? 1 : Object.keys(normalizedPaymentMethod.entities.paymentMethods)[0];
		const lastConfirmedMethodId = lastMethodIsInList ? selectedMethodID : fallbackMethodId;
		const { formTemplateCode } = normalizedPaymentMethod.entities.paymentMethods[lastConfirmedMethodId];
		/* property names are selected to be the same or as close to
        the original response as possible */
		const status = {
			methodId: lastConfirmedMethodId,
			formTemplateCode,
			autoSelectMethodId,
			isBankIdDeposit: isBankIdDeposit && isRegistration,
			autoskip: true
		};
		const spec = isBankIdDeposit || oneButtonFlow ? { ...bonusParams } : {};

		// #TODO: to make it immutable
		if (
			normalizedPaymentMethod.entities.paymentMethods['65'] &&
			normalizedPaymentMethod.entities.paymentMethods['65'].formTemplateCode === 'DOTPAY-BLIK'
		) {
			await handleBlikAlias(dispatch).then(blikAliasResponse => {
				status.blikCodeRequired = !blikAliasResponse?.result?.verified;
			});
		}
		if (
			normalizedPaymentMethod.entities.paymentMethods['86'] &&
			normalizedPaymentMethod.entities.paymentMethods['86'].formTemplateCode === 'TPAY-BLIK'
		) {
			await handleTpayBlikAlias(dispatch).then(blikAliasResponse => {
				status.blikCodeRequired = !blikAliasResponse?.result?.verified;
			});
		}

		const depositLimitPerFranchiseResponseVo = await getShowDepositLimitOption(dispatch);

		dispatch({
			type: SHOW_DEPOSIT_LIMIT_OPTION,
			payload: {
				showDepositLimitOptionLink: depositLimitPerFranchiseResponseVo.shouldShowDepositLimit,
				depositLimitCountryCode: depositLimitPerFranchiseResponseVo.depositLimitCountryCode
			}
		});

		const ccDefaultUrl = '/CreditCardIntegration/getCreditCardDepositFeesAndLimits/forMobile';
		dispatch({
			type: INIT_URL_FEES_AND_LIMITS,
			payload: ccDefaultUrl,
			pending: false
		});
		dispatch({
			type: HYDRATED_PAYMENT_METHODS,
			payload: {
				...normalizedPaymentMethod.entities,
				...normalizedFormTemplates.entities,
				lastUsedMethodDetails: response.lastUsedMethodDetails,
				status,
				spec,
				playerCurrency,
				fallbackPaymentMethodIds: response.fallbackPaymentMethodIds,
				hydrated: true,
				pending: false
			}
		});
		return dispatch(selectPaymentMethod(lastConfirmedMethodId));
	};
}

function updateSubmitButtonValidation(payload) {
	return dispatch => {
		return dispatch({
			type: UPDATE_SUBMIT_BUTTON_VALIDATION,
			payload
		});
	};
}

const paymentSelector = state => state.payment;
const statusSelector = state => state.payment.status;
const formTemplateTypesSelector = state => state.payment.formTemplateTypes;
const paymentMethodListSelector = state => state.payment.paymentMethods;
const paymentMethodSelector = createSelector(statusSelector, paymentMethodListSelector, (status, paymentMethods) => {
	const methodId = status?.methodId;
	if (!methodId || !paymentMethods[methodId]) {
		return {};
	}
	return paymentMethods[methodId];
});

const formTemplateCodeSelector = createSelector(
	paymentMethodSelector,
	paymentMethod => paymentMethod?.formTemplateCode || null
);
const formTemplateSelector = createSelector(
	formTemplateTypesSelector,
	formTemplateCodeSelector,
	(formTemplateTypes, formTemplateCode) => formTemplateTypes[formTemplateCode]
);

const paymentDetailSelector = createSelector(paymentSelector, payment => ({
	payment,
	status: payment.status,
	spec: payment.spec,
	kyc: payment.kyc
}));

/**
 * [hydratePaymentsRegistrationFlow To sequence of url-calls done during registration and with the correct sessioncookie is important, since a failing request might cause the browser to cancel the subsequent]
 * @return {Function} [inits the state with data for the complete payment flow]
 */
function hydratePaymentsRegistrationFlow() {
	return (
		/**
		 * @param {object} dispatch
		 */
		dispatch => {
			dispatch(getDepositOffers());
			dispatch(isDepositBonusOk());
			return dispatch(hydratePaymentMethods());
		}
	);
}
function hydrateBonusAndDeposit() {
	return dispatch => {
		dispatch(getDepositOffers());
		dispatch(isDepositBonusOk());
		return dispatch(hydratePaymentMethods());
	};
}
function hydratePaymentsDepositFlow() {
	return dispatch => {
		dispatch(hydratePendingWithdrawals());
		return dispatch(hydrateBonusAndDeposit());
	};
}

function storeShopItem({ bonusId, bonusPaymentMethods, bonusAddOnId = '', addOnOptionId = '' }) {
	return dispatch => {
		dispatch({
			type: SHOP_ITEM_FOR_DEPOSIT,
			payload: {
				bonusId,
				bonusPaymentMethods,
				baid: bonusAddOnId,
				oid: addOnOptionId
			}
		});
	};
}
function hydrateShopToDepositFlow({ bonusId, bonusPaymentMethods, bonusAddOnId = '', addOnOptionId = '' }) {
	// currently this is the same as only setting up the flow without bonus and pending-withdrawals.
	// Possibly additional actions are required to get all information from state.shop.
	return dispatch => {
		dispatch(
			storeShopItem({
				bonusId,
				bonusPaymentMethods,
				bonusAddOnId,
				addOnOptionId
			})
		);
		return dispatch(hydratePaymentMethods());
	};
}

function clearAll() {
	return dispatch => {
		dispatch({
			type: CLEAR_ALL,
			payload: {}
		});
	};
}

function clearBonusSpec() {
	return dispatch =>
		dispatch({
			type: CLEAR_BONUS_SPEC
		});
}

function updatePaymentData(payload) {
	return dispatch => dispatch({ type: UPDATE_PAYMENT_DATA, payload });
}

/**

 *
 * @param {any} [state={}]
 * @param {any} { type, payload }
 * @returns
 */
/* create read-only object: 'details' */
/* 'spec' will reflect what will be used in a possible submit */
function reducer(state = {}, { type, payload }) {
	switch (type) {
		case HYDRATED_PAYMENT_METHODS:
		case UPDATE_PAYMENT_DATA:
			return { ...state, ...payload };
		case GENERAL_ERROR: // clear all but the message
			return {
				generalError: true,
				generalErrorConfirmed: payload.confirmed,
				message: payload.message
			};
		case UPDATE_PAYMENT_METHODS:
			return {
				...state,
				status: {
					...state.status,
					...payload.status
				},
				spec: { ...payload.spec }
			};
		case CLEAR_RESULT:
			return {
				status: { ...payload.status },
				spec: { ...payload.spec }
			};
		case GET_PAYMENTS_TAGS:
			return {
				...state,
				paymentMethodTags: [...payload]
			};
		case HYDRATED_DETAILS:
			return {
				...state,
				status: {
					...state.status,
					accountIdx: payload
						? payload.hasAccounts && payload.lastUsedAccountIdx && parseInt(payload.lastUsedAccountIdx, 10)
						: null
				},
				methodDetails: { readonly: true, ...payload }
			};
		case UPDATE_AUTOSKIP:
			// for example used for fees and limits
			return {
				...state,
				...payload,
				status: {
					...state.status,
					...payload.status,
					autoskip: payload.status.autoskip
				}
			};
		case CLEAR_BONUS:
			return {
				...state,
				spec: {
					...state.spec,
					bid: null,
					bonusId: null,
					baid: null,
					oid: null
				},
				status: {
					...state.status,
					sum_bonus: false
				}
			};
		case CLEAR_BONUS_SPEC:
			return {
				...state,
				spec: {
					...state.spec,
					bid: null,
					bonusId: null,
					baid: null,
					oid: null,
					packageId: null
				}
			};
		case SELECT_BONUS:
			return {
				...state,
				spec: {
					...state.spec,
					...createBonusParams(payload)
				}
			};
		case SELECT_BONUS_PACKAGE:
			return {
				...state,
				spec: {
					...state.spec,
					bonusId: payload.bonusId,
					packageId: payload.packageId
				}
			};

		case SHOP_ITEM_FOR_DEPOSIT:
			return {
				...state,
				shopItem: {
					...state.shopItem,
					...payload
					// bonusId: payload.bonusId,
					// bonusPaymentMethods: payload.bonusPaymentMethods,
					// baid: payload.bonusAddOnId,
					// oid: payload.addOnOptionsId,
					// allowedForMethod: payload.allowedForMethod
				}
			};
		case VERIFY_AGE:
		case AGREEMENT_CHECK:
		case UPDATE_STATUS:
			// for example used for fees and limits
			return {
				...state,
				...payload,
				status: {
					...state.status,
					...payload.status
				}
			};
		case INIT_SPEC:
			/* create read-only object: 'details' */
			/* 'spec' will reflect what will be used in a possible submit */
			return {
				...state,
				spec: {
					...state.spec,
					...payload.spec
				},
				status: {
					...state.status,
					...payload.status
				}
			};
		case PROMPT_SECUREID: {
			/* this is used to display notifications regarding cvc, secuired, age, agreement, not allowed shop bonus */
			return {
				...state,
				status: { ...state.status, promptsecureid: payload }
			};
		}
		case PROMPT_KYC: {
			/* this is used to display notifications regarding kyc on deposit flow (used now for only crypto solutions) */
			return {
				...state,
				kyc: { ...state.kyc, ...payload }
			};
		}
		case APPLEPAY_PROMPT_SECUREID: {
			/* this is used to display age, agreement for apple pay before redirecting to amount page */
			return {
				...state,
				spec: { ...state.spec, ...payload.spec },
				status: { ...state.status, ...payload.status }
			};
		}
		case EXECUTE_DEPOSIT:
			return {
				...state,
				result: payload.result,
				spec: { ...state.spec, ...payload.spec, isMobileDevice: true }
			};
		case EXECUTE_DEPOSIT_PRECHECK:
			return { ...state, ...payload };
		case VALIDATE_SPEC:
			return {
				...state,
				spec: { ...state.spec, ...payload.spec },
				status: { ...state.status, ...payload.status }
			};
		case UPDATE_SPEC:
			return {
				...state,
				spec: { ...state.spec, ...payload }
			};
		case UPDATE_SUBMIT_BUTTON_VALIDATION:
			return {
				...state,
				spec: { ...state.spec, ...payload }
			};
		case UPDATE_AMOUNT:
			/* a prioritized order of selection shall be implemented here:
            1.userinput 2.accounthistory 3.lastAmount
            if a preset value is confirmed, it is set as userinput
            the spec.am shall contain the selected value
            state.status.amountuserinput */
			return {
				...state,
				status: {
					...state.status,
					amountuserinput: payload ? parseFloat(payload, 10) : ''
				},
				feesAndLimits: { pending: true },
				spec: { ...state.spec, am: payload ? parseFloat(payload, 10) : '' }
			};
		case INIT_URL_FEES_AND_LIMITS:
			return { ...state, ccLimitUrl: payload };
		case SHOW_DEPOSIT_LIMIT_OPTION:
			return { ...state, ...payload };
		case SELECT_DEFAULT_AMOUNT:
			return {
				...state,
				defaultDepositAmountsFormatted: {
					...state.defaultDepositAmountsFormatted,
					selected:
						(payload.defaultDepositAmountsFormatted && payload.defaultDepositAmountsFormatted.selected) ||
						(state.defaultDepositAmountsFormatted && state.defaultDepositAmountsFormatted.selected),
					pending: payload.defaultDepositAmountsFormatted && payload.defaultDepositAmountsFormatted.pending
				}
			};
		case UPDATE_FEES_AND_LIMITS:
			return {
				...state,
				status: {
					...state.status,
					...payload.summary
				},
				feesAndLimits: { ...payload.response },
				defaultDepositAmountsFormatted: {
					...state.defaultDepositAmountsFormatted,
					...payload.defaultDepositAmountsFormatted
				}
			};
		case SELECT_ACCOUNT:
			/* for a new account some input is reset: cvc, secureId */
			return {
				...state,
				status: { ...state.status, accountIdx: parseInt(payload, 10) }
			};
		case LOGOUT:
		case CLEAR_ALL:
			/* to be able to reset state when switching stories in storybook */
			return {};
		default:
			return state;
	}
}

reducerRegistry.register(REDUCER_NAME, reducer);

/**
 * The api:
 * The current use in deposit flow:
 *
 * Entrypoints:
 * the component 'flow/deposit-flow.js' initiates the creation of all data. It
 * differentiates between normal default flow from 'account' menu, and registration to
 * deposit, and shop to deposit. Differences exist since they enter the flow at different
 * steps, and ignore or include fetches for bonus and pendingwithdrawals.
 * These methods fetches the list of deposit-payment-method.json and subsequent calls to
 * fetch method-details.
 *
 * hydratePaymentsDepositFlow,
 * hydratePaymentsRegistrationFlow,
 * hydrateShopToDepositFlow,
 *
 * As a consequence this starts a chain of fetches to populate
 * 'status' and 'spec' and more parts of the store.
 * The 'status' part is intended to keep slightly more permanent data, and keeps info that
 * does not change much when selecting a new deposit method, for example
 * agreement-check, deposit-summary.
 * The 'spec' part is intended to keep arguments that suits the specific deposit method.
 * For example the fetch to execute a creditcard deposit requires info about creditcardnumber ("cn"),
 * and neteller instead requires a "externalid".
 *
 * Other methods are used to modify the store depending on user activities.
 * As example "selectPaymentMethod" is triggered when a new deposit method is selected in the
 * deposit-method.js component.
 *
 *
 */
export {
	CLEAR_ALL,
	CLEAR_BONUS_SPEC,
	SELECT_PAYMENT_METHOD,
	changeLastUsedAccountIdx,
	clearAll,
	clearBonusSpec,
	confirmGeneralError,
	formTemplateSelector,
	getCVVBypass,
	getPaymentMethodTags,
	hydratePaymentMethods,
	hydratePaymentsDepositFlow,
	hydratePaymentsRegistrationFlow,
	hydrateShopToDepositFlow,
	paymentDetailSelector,
	paymentMethodSelector,
	storeShopItem,
	updatePaymentData,
	updatePaymentStatusSpec,
	updateSubmitButtonValidation
};

export default reducer;
